import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Loader2, ArrowRight, MapPin, User, DollarSign, Send, ChevronLeft,
  CheckCircle, Clock, Package, AlertTriangle, Wallet, Download
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_DISPLAY = {
  cash: { name: 'Cash Delivery', icon: '💵', desc: 'Entrega de efectivo en Cuba' },
  efectivo: { name: 'Efectivo', icon: '💶', desc: 'Envio de efectivo' },
  zelle: { name: 'Zelle', icon: '⚡', desc: 'Transferencia via Zelle' },
  mlc: { name: 'MLC', icon: '🏦', desc: 'Moneda Libremente Convertible' },
  moneda_nacional: { name: 'Moneda Nacional', icon: '🇨🇺', desc: 'Peso cubano (CUP)' },
  tropical: { name: 'Tropical', icon: '🌴', desc: 'Servicio Tropical' },
};

const STATUS_MAP = {
  pending_review: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  confirmando: { label: 'Confirmando', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
  completado: { label: 'Completado', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rechazado', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  api_error: { label: 'Error', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

const B2BTransfersPage = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('channels');
  const [channels, setChannels] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);

  const [amount, setAmount] = useState('');
  const [quote, setQuote] = useState(null);
  const [quotingLoading, setQuotingLoading] = useState(false);

  const [provinces, setProvinces] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const [receiverName, setReceiverName] = useState('');
  const [receiverPhone, setReceiverPhone] = useState('');
  const [receiverCity, setReceiverCity] = useState('');
  const [receiverProvince, setReceiverProvince] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [orders, setOrders] = useState([]);
  const [showOrders, setShowOrders] = useState(false);

  const loadChannels = async () => {
    try {
      const res = await axios.get(`${API}/b2b/channels`, { headers: getAuthHeaders() });
      setChannels(res.data);
    } catch { toast.error('Error al cargar canales'); }
    finally { setLoadingChannels(false); }
  };

  const loadWallet = async () => {
    try {
      const res = await axios.get(`${API}/wallet/summary`, { headers: getAuthHeaders() });
      setWalletBalance(res.data.balance || 0);
    } catch { setWalletBalance(0); }
  };

  const loadProvinces = async () => {
    try {
      const res = await axios.get(`${API}/b2b/locations/provinces`, { headers: getAuthHeaders() });
      setProvinces(res.data.provinces || []);
    } catch (e) { console.error(e); }
  };

  const loadLocations = async (province) => {
    setLoadingLocations(true);
    try {
      const res = await axios.get(`${API}/b2b/locations`, { headers: getAuthHeaders(), params: { province } });
      setLocations(res.data.locations || []);
    } catch (e) { console.error(e); }
    finally { setLoadingLocations(false); }
  };

  const loadMyOrders = async () => {
    try {
      const res = await axios.get(`${API}/b2b/my-orders`, { headers: getAuthHeaders() });
      setOrders(res.data || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadChannels(); loadMyOrders(); loadWallet(); }, []);

  const fetchQuote = useCallback(async (amt, ch) => {
    if (!amt || amt <= 0 || !ch) return;
    setQuotingLoading(true);
    try {
      const res = await axios.get(`${API}/b2b/fee-quote`, {
        headers: getAuthHeaders(), params: { amount: amt, channel: ch }
      });
      setQuote(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cotizar');
      setQuote(null);
    } finally { setQuotingLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!amount || !selectedChannel) return;
    const timer = setTimeout(() => fetchQuote(parseFloat(amount), selectedChannel.channel), 500);
    return () => clearTimeout(timer);
  }, [amount, selectedChannel, fetchQuote]);

  useEffect(() => { if (selectedProvince) loadLocations(selectedProvince); }, [selectedProvince]);

  const selectChannel = (ch) => {
    setSelectedChannel(ch);
    setStep('form');
    setAmount('');
    setQuote(null);
    setSelectedProvince('');
    setSelectedLocation(null);
    setReceiverName(''); setReceiverPhone(''); setReceiverCity(''); setReceiverProvince('');
    loadProvinces();
  };

  const submitTransfer = async () => {
    if (!quote || !selectedChannel) return;
    setSubmitting(true);
    try {
      const payload = {
        amount: parseFloat(amount),
        service_channel: selectedChannel.channel,
        receiver_name: receiverName,
        receiver_phone: receiverPhone,
        receiver_city: receiverCity,
        receiver_province: receiverProvince,
        province: selectedProvince || '',
        municipality: selectedLocation?.Municipio || selectedLocation?.municipality || '',
        location_id: selectedLocation?.id || selectedLocation?.Id || null,
      };

      const res = await axios.post(`${API}/b2b/transfers`, payload, { headers: getAuthHeaders() });
      setStep('success');
      setWalletBalance(res.data.new_balance || 0);
      loadMyOrders();
      toast.success(res.data.message);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      if (detail.includes('Saldo insuficiente')) {
        toast.error(detail);
      } else {
        toast.error('Error: ' + detail);
      }
    } finally { setSubmitting(false); }
  };

  const reset = () => {
    setStep('channels');
    setSelectedChannel(null);
    setAmount('');
    setQuote(null);
    setSelectedProvince('');
    setSelectedLocation(null);
    setReceiverName(''); setReceiverPhone(''); setReceiverCity(''); setReceiverProvince('');
    loadWallet();
  };

  const insufficientFunds = quote && walletBalance < quote.total_to_pay;

  if (loadingChannels) return (
    <div className="flex items-center justify-center py-20" data-testid="transfers-loading">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="b2b-transfers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Envios y Transferencias</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Envia dinero a traves de nuestros canales</p>
        </div>
        <div className="flex gap-2 items-center">
          {/* Wallet Badge */}
          <Card className="px-3 py-2 flex items-center gap-2" data-testid="wallet-balance-badge">
            <Wallet className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">${walletBalance.toFixed(2)}</span>
          </Card>
          <Button variant="outline" size="sm" onClick={() => { setShowOrders(!showOrders); loadMyOrders(); }} data-testid="toggle-my-orders">
            <Package className="w-4 h-4 mr-2" /> Mis Ordenes ({orders.length})
          </Button>
        </div>
      </div>

      {/* My Orders List */}
      {showOrders && (
        <Card className="p-4" data-testid="my-orders-list">
          <h3 className="font-semibold text-slate-800 dark:text-white mb-3">Mis Ordenes Recientes</h3>
          {orders.length === 0 ? (
            <p className="text-sm text-slate-400">No tienes ordenes aun</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {orders.map(o => {
                const st = STATUS_MAP[o.status] || STATUS_MAP.pending_review;
                return (
                  <div key={o.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg" data-testid={`my-order-${o.id}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{o.channel} — ${Number(o.amount).toFixed(2)}</p>
                      <p className="text-xs text-slate-400">{new Date(o.created_at).toLocaleString()}</p>
                      {o.receiver_data?.full_name && <p className="text-xs text-slate-500">Para: {o.receiver_data.full_name}</p>}
                    </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                    {o.status !== 'pending_review' && o.status !== 'rejected' && (
                      <a href={`${API}/b2b/orders/${o.id}/receipt`} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800" title="Descargar recibo" data-testid={`receipt-${o.id}`}>
                        <Download className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* Step 1: Channel Selection */}
      {step === 'channels' && (
        <div>
          {walletBalance <= 0 && (
            <Card className="p-4 mb-4 bg-amber-50 dark:bg-amber-900/20 border-amber-300" data-testid="no-balance-warning">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Necesitas saldo en tu wallet para solicitar servicios</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">Recarga tu wallet primero desde la seccion de Wallet</p>
                </div>
                <Button size="sm" onClick={() => navigate('/cliente/wallet')} data-testid="go-to-wallet-btn">Recargar</Button>
              </div>
            </Card>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="channel-selection">
            {channels.map(ch => {
              const display = CHANNEL_DISPLAY[ch.channel] || { name: ch.display_name, icon: '📦', desc: '' };
              return (
                <Card key={ch.channel} className="p-5 cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all" onClick={() => selectChannel(ch)} data-testid={`channel-card-${ch.channel}`}>
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">{display.icon}</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800 dark:text-white">{display.name}</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{display.desc}</p>
                      <div className="mt-3">
                        <span className="text-xs text-slate-400">${ch.min_amount} - ${ch.max_amount > 90000 ? '∞' : ch.max_amount} USD</span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
            {channels.length === 0 && (
              <Card className="col-span-full p-12 text-center">
                <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <p className="text-slate-600 dark:text-slate-300 font-medium">No hay canales disponibles</p>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Amount + Calculation + Receiver */}
      {step === 'form' && selectedChannel && (
        <div className="max-w-2xl mx-auto space-y-4" data-testid="transfer-form-step">
          <Button variant="ghost" size="sm" onClick={reset} data-testid="back-to-channels">
            <ChevronLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-2xl">{(CHANNEL_DISPLAY[selectedChannel.channel] || {}).icon || '📦'}</span>
              <div className="flex-1">
                <h2 className="font-bold text-lg text-slate-800 dark:text-white">{(CHANNEL_DISPLAY[selectedChannel.channel] || {}).name || selectedChannel.display_name}</h2>
                <p className="text-xs text-slate-400">Rango: ${selectedChannel.min_amount} — ${selectedChannel.max_amount > 90000 ? 'Sin limite' : `$${selectedChannel.max_amount}`}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">Tu saldo</p>
                <p className="text-sm font-bold text-emerald-700">${walletBalance.toFixed(2)}</p>
              </div>
            </div>

            <div className="space-y-1 mb-4">
              <Label htmlFor="amount">Monto a enviar (USD)</Label>
              <Input id="amount" type="number" step="0.01" min={selectedChannel.min_amount} max={selectedChannel.max_amount} placeholder={`Min: $${selectedChannel.min_amount}`} value={amount} onChange={e => setAmount(e.target.value)} className="text-lg font-semibold" data-testid="transfer-amount-input" />
            </div>

            {quotingLoading && <div className="flex items-center gap-2 text-sm text-slate-400 mb-4"><Loader2 className="w-4 h-4 animate-spin" /> Cotizando...</div>}

            {/* Calculation Table */}
            {quote && !quotingLoading && (
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800 p-5 mb-4" data-testid="fee-quote-card">
                <h4 className="text-sm font-bold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-1"><DollarSign className="w-4 h-4" /> Resumen del Envio</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1"><span className="text-slate-600 dark:text-slate-300">Monto a enviar:</span><span className="font-semibold">${Number(quote.amount).toFixed(2)}</span></div>
                  <div className="flex justify-between py-1"><span className="text-slate-600 dark:text-slate-300">Costo del servicio:</span><span className="font-medium text-amber-700">${Number(quote.total_fee).toFixed(2)}</span></div>
                  <hr className="my-1 border-blue-200 dark:border-blue-700" />
                  <div className="flex justify-between text-base font-bold py-1"><span className="text-slate-800 dark:text-white">Se descontara de tu wallet:</span><span className="text-blue-700 dark:text-blue-300">${Number(quote.total_to_pay).toFixed(2)}</span></div>
                  <div className="flex justify-between py-1 bg-emerald-50 dark:bg-emerald-900/20 rounded px-2">
                    <span className="text-emerald-700 dark:text-emerald-300 font-medium">Destinatario recibe:</span>
                    <span className="text-emerald-700 dark:text-emerald-300 font-bold text-base">${Number(quote.recipient_receives).toFixed(2)}</span>
                  </div>
                  {insufficientFunds && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-red-700 dark:text-red-300 text-xs">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      <span>Saldo insuficiente. Tu balance: ${walletBalance.toFixed(2)}. <button onClick={() => navigate('/cliente/wallet')} className="underline font-medium">Recargar wallet</button></span>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Receiver Info */}
            <div className="space-y-3 mb-4">
              <Label className="flex items-center gap-1 font-semibold"><User className="w-4 h-4" /> Datos del Receptor</Label>
              <Input placeholder="Nombre completo *" value={receiverName} onChange={e => setReceiverName(e.target.value)} data-testid="receiver-name" />
              <Input placeholder="Telefono" value={receiverPhone} onChange={e => setReceiverPhone(e.target.value)} data-testid="receiver-phone" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Ciudad" value={receiverCity} onChange={e => setReceiverCity(e.target.value)} />
                <Input placeholder="Provincia" value={receiverProvince} onChange={e => setReceiverProvince(e.target.value)} />
              </div>
            </div>

            {/* Province + Location (optional) */}
            <div className="space-y-3 mb-4">
              <Label className="flex items-center gap-1"><MapPin className="w-4 h-4" /> Ubicacion de entrega (opcional)</Label>
              <select value={selectedProvince} onChange={e => { setSelectedProvince(e.target.value); setSelectedLocation(null); }} className="w-full border rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-800 dark:text-white" data-testid="province-select">
                <option value="">Seleccionar provincia...</option>
                {provinces.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              {selectedProvince && (
                loadingLocations ? <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Cargando...</div> :
                locations.length === 0 ? <p className="text-sm text-amber-600">No hay ubicaciones disponibles</p> :
                <div className="max-h-36 overflow-y-auto space-y-1" data-testid="locations-list">
                  {locations.map((loc, i) => {
                    const locName = loc.Nombre || loc.name || loc.Municipio || `Ubicacion ${i + 1}`;
                    const isSelected = selectedLocation && (selectedLocation.id || selectedLocation.Id) === (loc.id || loc.Id);
                    return (
                      <button key={loc.id || loc.Id || i} className={`w-full text-left p-2 rounded-lg text-sm border transition-all ${isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'}`} onClick={() => setSelectedLocation(loc)} data-testid={`location-${i}`}>
                        <div className="flex items-center gap-2">
                          <MapPin className={`w-4 h-4 ${isSelected ? 'text-blue-500' : 'text-slate-400'}`} />
                          <span className="font-medium text-slate-700 dark:text-slate-200">{locName}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <Button className="w-full" size="lg" disabled={!quote || !amount || !receiverName || insufficientFunds} onClick={() => setStep('confirm')} data-testid="continue-to-confirm">
              <ArrowRight className="w-4 h-4 mr-2" /> Confirmar Envio
            </Button>
          </Card>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === 'confirm' && quote && (
        <div className="max-w-lg mx-auto" data-testid="confirm-step">
          <Button variant="ghost" size="sm" onClick={() => setStep('form')} className="mb-4" data-testid="back-to-form">
            <ChevronLeft className="w-4 h-4 mr-1" /> Editar
          </Button>
          <Card className="p-6 space-y-4">
            <h2 className="font-bold text-lg text-slate-800 dark:text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" /> Confirmar Transferencia
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Canal:</span><span className="font-medium capitalize">{selectedChannel?.display_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Monto a enviar:</span><span className="font-medium">${Number(quote.amount).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Costo del servicio:</span><span className="text-amber-600">${Number(quote.total_fee).toFixed(2)}</span></div>
              <hr />
              <div className="flex justify-between text-lg font-bold"><span>Se descontara:</span><span className="text-blue-700 dark:text-blue-400">${Number(quote.total_to_pay).toFixed(2)}</span></div>
              <div className="flex justify-between bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded"><span className="text-emerald-700 dark:text-emerald-300">Destinatario recibe:</span><span className="font-bold text-emerald-700 dark:text-emerald-300">${Number(quote.recipient_receives).toFixed(2)}</span></div>
              <hr />
              {receiverName && <div className="flex justify-between"><span className="text-slate-500">Receptor:</span><span className="font-medium">{receiverName}</span></div>}
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs text-blue-700 dark:text-blue-300">
              El monto sera descontado de tu wallet inmediatamente. Tu solicitud sera procesada por el administrador.
            </div>
            <Button className="w-full" size="lg" onClick={submitTransfer} disabled={submitting} data-testid="submit-transfer-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Confirmar y Pagar ${quote ? Number(quote.total_to_pay).toFixed(2) : ''}
            </Button>
          </Card>
        </div>
      )}

      {/* Step 4: Success */}
      {step === 'success' && (
        <div className="max-w-lg mx-auto text-center" data-testid="success-step">
          <Card className="p-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Transferencia Enviada</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">El monto fue descontado de tu wallet y la solicitud esta siendo procesada.</p>
            <p className="text-sm font-medium text-emerald-700 mb-6">Nuevo saldo: ${walletBalance.toFixed(2)}</p>
            <Button onClick={reset} className="w-full" data-testid="new-transfer-btn">Nueva Transferencia</Button>
          </Card>
        </div>
      )}
    </div>
  );
};

export default B2BTransfersPage;
